import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import WizardStepLayout from '../WizardStepLayout';
import { WIZARD_ACTIONS } from '../constants';

const StoryStep = ({
  state,
  dispatch,
  multiFile,
  onNext,
  onBack,
}) => {
  const inputRefs = useRef({});
  const [dragOverFieldId, setDragOverFieldId] = useState(null);
  const [thumbUrls, setThumbUrls] = useState({});

  const fileFields = useMemo(
    () =>
      (state.formFields || []).filter(
        (f) => String(f.field_type || '').toLowerCase() === 'file',
      ),
    [state.formFields],
  );

  const { buckets, addFiles, removeFile, setCaption, totalCount } = multiFile;

  useEffect(() => {
    const next = {};
    Object.entries(buckets).forEach(([fieldId, entries]) => {
      (entries || []).forEach((entry, i) => {
        const k = `${fieldId}-${i}`;
        if (entry.file?.type?.startsWith('image/')) {
          next[k] = URL.createObjectURL(entry.file);
        }
      });
    });
    setThumbUrls(next);
    return () => {
      Object.values(next).forEach((u) => {
        if (u) URL.revokeObjectURL(u);
      });
    };
  }, [buckets]);

  const handleTextChange = useCallback(
    (e) => {
      dispatch({ type: WIZARD_ACTIONS.SET_STORY_TEXT, payload: e.target.value });
    },
    [dispatch],
  );

  const handleFilesSelected = useCallback(
    (fieldId, fileList) => {
      addFiles(fieldId, fileList);
    },
    [addFiles],
  );

  const handleInputChange = useCallback(
    (fieldId, e) => {
      if (e.target.files && e.target.files.length > 0) {
        // CRITICAL: copy to a real array BEFORE clearing the input. `e.target.files`
        // is a live FileList bound to the <input>, and `e.target.value = ''` empties
        // it in place. Because useReducer dispatches are batched, the reducer reads
        // its `fileList` payload AFTER this handler returns — by which time, if we
        // pass the live FileList directly, it has length 0 and zero entries get
        // added. That is the difference between drag-drop (works — dataTransfer
        // owns its own FileList) and click-to-browse (silently no-ops). Snapshot
        // first, then clear, then dispatch.
        const picked = Array.from(e.target.files);
        e.target.value = '';
        handleFilesSelected(fieldId, picked);
      }
    },
    [handleFilesSelected],
  );

  const handleDrop = useCallback(
    (fieldId, e) => {
      e.preventDefault();
      setDragOverFieldId(null);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        // Copy here too for parity / defense-in-depth: dataTransfer.files becomes
        // invalid after the drop event finishes propagating in some browsers.
        handleFilesSelected(fieldId, Array.from(e.dataTransfer.files));
      }
    },
    [handleFilesSelected],
  );

  const handleRemove = useCallback(
    (fieldId, index) => {
      removeFile(fieldId, index);
    },
    [removeFile],
  );

  const hasStory = Boolean(state.storyText && state.storyText.trim());
  const hasFiles = totalCount > 0;
  const hasContent = hasStory || hasFiles;

  const nextDisabled = !hasContent;
  const nextLabel = 'Next \u2192';

  return (
    <WizardStepLayout
      stepNumber={4}
      onNext={onNext}
      onBack={onBack}
      nextDisabled={nextDisabled}
      nextLabel={nextLabel}
      showBack
    >
      <textarea
        className="wizard__textarea"
        rows={4}
        placeholder="Write your memory or story about this place..."
        value={state.storyText}
        onChange={handleTextChange}
      />

      {fileFields.map((field, fieldIdx) => {
        const fieldId = field.field_id;
        const entries = buckets[String(fieldId)] || [];
        const isDragOver = dragOverFieldId === fieldId;

        return (
          <div
            key={fieldId}
            className="wizard__file-field-block"
            style={{ marginTop: fieldIdx === 0 ? 0 : 12 }}
          >
            {fileFields.length > 1 && (
              <p className="wizard__helper-text wizard__helper-text--field-label">
                {field.field_name || 'Attachments'}
              </p>
            )}
            <button
              type="button"
              className={
                'wizard__drop-zone' +
                (isDragOver ? ' wizard__drop-zone--drag-over' : '')
              }
              aria-label={`Upload ${field.field_name || 'attachments'}`}
              onClick={() => inputRefs.current[fieldId]?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverFieldId(fieldId);
              }}
              onDragLeave={() => setDragOverFieldId(null)}
              onDrop={(e) => handleDrop(fieldId, e)}
            >
              Drop photos, documents, or videos here &mdash; or click to browse
            </button>
            <input
              ref={(el) => {
                inputRefs.current[fieldId] = el;
              }}
              type="file"
              multiple
              accept="image/*,audio/*,.pdf,.mp4,.mov,.avi"
              aria-label={`${field.field_name || 'Attachments'} files`}
              style={{ display: 'none' }}
              onChange={(e) => handleInputChange(fieldId, e)}
            />
            {entries.length > 0 && (
              <div className="wizard__file-list">
                {entries.map((entry, i) => {
                  const thumbKey = `${fieldId}-${i}`;
                  const thumb = thumbUrls[thumbKey];
                  return (
                    <div key={`${entry.file.name}-${i}`} className="wizard__file-block">
                      {thumb ? (
                        <img className="wizard__file-thumb" src={thumb} alt="" />
                      ) : null}
                      <div className="wizard__file-item">
                        <span className="wizard__file-name">{entry.file.name}</span>
                        <button
                          type="button"
                          className="wizard__file-remove"
                          onClick={() => handleRemove(fieldId, i)}
                        >
                          Remove
                        </button>
                      </div>
                      <label
                        className="wizard__file-caption-label"
                        htmlFor={`wizard-caption-${fieldId}-${i}`}
                      >
                        Caption
                      </label>
                      <input
                        id={`wizard-caption-${fieldId}-${i}`}
                        className="wizard__file-caption-input wizard__file-caption-input--fixed"
                        type="text"
                        placeholder="Optional caption for this file"
                        value={entry.caption}
                        onChange={(e) => setCaption(fieldId, i, e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <p className="wizard__helper-text">
        Supported: jpg, png, pdf, mp4. Max 50mb per file.
      </p>
    </WizardStepLayout>
  );
};

export default React.memo(StoryStep);
